import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Camera, MapPin, Users, Monitor, X, ChevronLeft, ChevronRight } from 'lucide-react';

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages = [
    {
      id: 1,
      url: "https://customer-assets.emergentagent.com/job_wellcontrol-edu/artifacts/bpo9rgrr_Phoenix%20-%201.png",
      title: "Modern Training Classroom",
      description: "State-of-the-art classroom setup with comfortable seating for up to 15 participants",
      category: "Classroom"
    },
    {
      id: 2,
      url: "https://customer-assets.emergentagent.com/job_wellcontrol-edu/artifacts/gwq3dwnd_Phoenix-%202.png",
      title: "Interactive Learning Environment", 
      description: "Collaborative learning space with modern furniture and audiovisual equipment",
      category: "Facilities"
    },
    {
      id: 3,
      url: "https://customer-assets.emergentagent.com/job_wellcontrol-edu/artifacts/l00ofhaw_Phoenix%203.png",
      title: "Expert-Led Training Sessions",
      description: "Professional instructors delivering comprehensive drilling safety training",
      category: "Training"
    },
    {
      id: 4,
      url: "https://customer-assets.emergentagent.com/job_wellcontrol-edu/artifacts/ky1dkp2h_Phoenix%204.png",
      title: "Engaging Learning Experience",
      description: "Interactive training sessions with industry professionals and real-world scenarios",
      category: "Training"
    },
    {
      id: 5,
      url: "https://customer-assets.emergentagent.com/job_wellcontrol-edu/artifacts/zmxwlpqq_Phoenix%205.png",
      title: "Advanced Training Facility",
      description: "Professional training environment equipped with modern technology and safety equipment",
      category: "Facilities"
    },
    {
      id: 6,
      url: "https://customer-assets.emergentagent.com/job_wellcontrol-edu/artifacts/fbln39w2_WhatsApp%20Image%202025-10-13%20at%2019.39.43.jpeg",
      title: "Phoenix WellControl Academy",
      description: "Our training center located in Mehsana, Gujarat - Contact us at +91-7622952967",
      category: "Location"
    }
  ];

  const categories = ["All", "Classroom", "Facilities", "Training", "Location"];
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredImages = activeCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openImageModal = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex + 1 >= filteredImages.length ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex - 1 < 0 ? filteredImages.length - 1 : currentIndex - 1;
    }
    
    setSelectedImage(filteredImages[newIndex]);
    setCurrentImageIndex(newIndex);
  };

  return (
    <section id="gallery" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-blue-100 px-6 py-3 rounded-full mb-6">
            <Camera className="h-5 w-5 text-blue-600" />
            <span className="text-blue-700 font-semibold">Training Facilities</span>
          </div>
          <h2 className="text-5xl font-black text-gray-900 mb-6 leading-tight">
            Our Training <br />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Center Gallery
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Take a look inside our state-of-the-art training facilities in Gujarat, India. Our modern classrooms 
            and interactive learning environments are designed to provide the best drilling safety education experience.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setActiveCategory(category)}
              variant={activeCategory === category ? "default" : "outline"}
              className={`rounded-full px-6 py-2 font-semibold transition-all hover:scale-105 ${
                activeCategory === category
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                  : "border-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              }`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredImages.map((image, index) => (
            <Card 
              key={image.id} 
              className="group border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden cursor-pointer"
              onClick={() => openImageModal(image, index)}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={image.url} 
                  alt={image.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="flex items-center space-x-2 mb-2">
                      <Camera className="h-4 w-4" />
                      <span className="text-sm font-medium">{image.category}</span>
                    </div>
                    <h3 className="text-lg font-bold">{image.title}</h3>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {image.title}
                    </h3>
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                      {image.category}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {image.description}
                  </p>
                  <div className="flex items-center space-x-2 text-blue-600 group-hover:text-blue-700 transition-colors">
                    <Camera className="h-4 w-4" />
                    <span className="text-sm font-medium">Click to view larger</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
            <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-black border-0">
              <div className="relative">
                <img 
                  src={selectedImage.url} 
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
                
                {/* Navigation Buttons */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>

                {/* Close Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                >
                  <X className="h-5 w-5" />
                </Button>

                {/* Image Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white p-6">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="bg-blue-500 px-3 py-1 rounded-full text-xs font-semibold">
                        {selectedImage.category}
                      </span>
                      <span className="text-sm opacity-75">
                        {currentImageIndex + 1} of {filteredImages.length}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold">{selectedImage.title}</h3>
                    <p className="text-lg opacity-90">{selectedImage.description}</p>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Visit Our Training Center?
            </h3>
            <p className="text-gray-600 mb-6 text-lg">
              Experience our world-class facilities firsthand. Contact us to schedule a visit or enroll in our training programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-8 py-3 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg"
              >
                <MapPin className="h-5 w-5 mr-2" />
                Schedule a Visit
              </Button>
              <Button 
                onClick={() => document.getElementById('courses').scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-blue-300 text-blue-700 hover:bg-blue-50 px-8 py-3 rounded-xl font-bold text-lg transition-all hover:scale-105"
              >
                <Users className="h-5 w-5 mr-2" />
                View Training Programs
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};